import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Pencil, Trash2, Loader2, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  title: { en: 'What Arvexis Remembers', es: 'Lo que Arvexis recuerda' },
  seeAll: { en: 'See everything she remembers', es: 'Ver todo lo que recuerda' },
  back: { en: 'Back to the summary', es: 'Volver al resumen' },
  everything: { en: 'Everything she remembers', es: 'Todo lo que recuerda' },
  yourWords: { en: 'In your words', es: 'En tus palabras' },
  correct: { en: 'Correct', es: 'Corregir' },
  forget: { en: 'Forget this', es: 'Olvidar esto' },
  confirmed: { en: 'you confirmed this', es: 'lo confirmaste' },
  corrected: { en: 'you corrected this', es: 'lo corregiste' },
  fixTitle: { en: 'What should it say instead?', es: '¿Qué debería decir?' },
  save: { en: 'Save the correction', es: 'Guardar la corrección' },
  cancel: { en: 'Cancel', es: 'Cancelar' },
  forgetTitle: { en: 'Forget this?', es: '¿Olvidar esto?' },
  forgetBody: {
    en: 'She will drop this and the words behind it, and will not bring it up again.',
    es: 'Lo eliminará junto con las palabras detrás, y no volverá a mencionarlo.'
  },
  forgetGo: { en: 'Yes, forget it', es: 'Sí, olvídalo' },
  empty: {
    en: 'Nothing yet. She only keeps what you actually tell her.',
    es: 'Nada aún. Solo guarda lo que realmente le cuentas.'
  },
};

const t = (key, lang) => T[key][lang === 'es' ? 'es' : 'en'];

const MemoryCard = ({ item, lang, onChanged }) => {
  const [mode, setMode] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const act = async (path, body) => {
    setBusy(true);
    try {
      await fetch(`${API}/api/companion/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      setMode(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const subject = item.what_she_remembers || '';

  return (
    <div
      className="rounded-2xl p-4 mb-3"
      style={{ background: 'rgba(255,255,255,.035)', border: '1px solid rgba(255,255,255,.07)' }}
      data-testid="memory-card"
    >
      <div className="flex gap-1.5 mb-2">
        {item.you_confirmed_this && (
          <span className="text-[10px] uppercase tracking-wider text-white/35 border border-white/10 rounded-full px-2 py-0.5">
            {t('confirmed', lang)}
          </span>
        )}
        {item.you_corrected_this && (
          <span className="text-[10px] uppercase tracking-wider text-white/35 border border-white/10 rounded-full px-2 py-0.5">
            {t('corrected', lang)}
          </span>
        )}
      </div>

      <p className="text-white/85 text-sm leading-relaxed">{subject}</p>

      {item.your_words && item.your_words !== subject && (
        <div className="mt-3 pl-3 border-l" style={{ borderColor: 'rgba(217,178,106,.5)' }}>
          <p className="text-[10px] uppercase tracking-wider text-white/30 mb-0.5">{t('yourWords', lang)}</p>
          <p className="text-white/55 text-sm italic leading-relaxed">“{item.your_words}”</p>
        </div>
      )}

      <p className="text-xs text-white/30 mt-3">
        {[item.kind, item.where_it_came_from && `from ${item.where_it_came_from}`,
          item.first_heard && new Date(item.first_heard).toLocaleDateString(lang === 'es' ? 'es' : 'en', { day: 'numeric', month: 'long', year: 'numeric' })]
          .filter(Boolean).join(' · ')}
      </p>

      {mode === null && (
        <div className="flex gap-4 mt-3">
          <button
            onClick={() => { setDraft(subject); setMode('correct'); }}
            className="flex items-center gap-1.5 text-xs text-white/45 hover:text-white/80 transition-colors"
            data-testid="memory-correct-btn"
          >
            <Pencil className="w-3 h-3" /> {t('correct', lang)}
          </button>
          <button
            onClick={() => setMode('forget')}
            className="flex items-center gap-1.5 text-xs text-white/45 hover:text-white/80 transition-colors"
            data-testid="memory-forget-btn"
          >
            <Trash2 className="w-3 h-3" /> {t('forget', lang)}
          </button>
        </div>
      )}

      {mode === 'correct' && (
        <div className="mt-3" data-testid="memory-correct-form">
          <p className="text-xs text-white/40 mb-1.5">{t('fixTitle', lang)}</p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            className="w-full text-sm rounded-xl px-3 py-2 resize-none focus:outline-none"
            style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.08)', color: 'rgba(255,255,255,.85)' }}
            data-testid="memory-correct-input"
          />
          <div className="flex gap-3 mt-2">
            <button
              disabled={busy || !draft.trim() || draft === subject}
              onClick={() => act('correct', { incorrect: subject, correct: draft.trim() })}
              className="text-xs text-white/80 disabled:text-white/25"
              data-testid="memory-correct-save"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('save', lang)}
            </button>
            <button onClick={() => setMode(null)} className="text-xs text-white/35" data-testid="memory-correct-cancel">
              {t('cancel', lang)}
            </button>
          </div>
        </div>
      )}

      {mode === 'forget' && (
        <div className="mt-3" data-testid="memory-forget-confirm">
          <p className="text-sm text-white/70">{t('forgetTitle', lang)}</p>
          <p className="text-xs text-white/40 mt-1 leading-relaxed">{t('forgetBody', lang)}</p>
          <div className="flex gap-3 mt-2">
            <button
              disabled={busy}
              onClick={() => act('forget', { topic: subject })}
              className="text-xs text-amber-300/80"
              data-testid="memory-forget-confirm-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('forgetGo', lang)}
            </button>
            <button onClick={() => setMode(null)} className="text-xs text-white/35">{t('cancel', lang)}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const MemorySummary = ({ lang = 'en', onClose }) => {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/memory/summary`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] overflow-y-auto"
      style={{ background: 'rgba(8,10,15,.94)', backdropFilter: 'blur(18px)' }}
      data-testid="memory-summary-overlay"
    >
      <div className="max-w-2xl mx-auto px-5 pt-6" style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom))' }}>
        <div className="flex items-center justify-between mb-8">
          {detail ? (
            <button onClick={() => setDetail(false)} className="flex items-center gap-2 text-sm text-white/45 hover:text-white/80" data-testid="memory-back-btn">
              <ArrowLeft className="w-4 h-4" /> {t('back', lang)}
            </button>
          ) : <span />}
          <button onClick={onClose} className="p-2 -mr-2 text-white/35 hover:text-white/70" data-testid="memory-summary-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && <Loader2 className="w-5 h-5 animate-spin text-white/30 mx-auto mt-16" />}

        {!loading && data && (
          <AnimatePresence mode="wait">
            {!detail ? (
              <motion.div key="summary" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <h1
                  className="text-3xl sm:text-4xl text-white/90 mb-6 leading-tight"
                  style={{ fontFamily: '"Iowan Old Style", Palatino, Georgia, serif' }}
                >
                  {t('title', lang)}
                </h1>
                <p className="text-white/50 text-sm leading-relaxed mb-7">{data.opening}</p>

                <div className="space-y-4" data-testid="memory-summary-lines">
                  {data.lines.length === 0 && <p className="text-white/45 text-sm">{t('empty', lang)}</p>}
                  {data.lines.map((line, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.06 * i, duration: 0.4 }}
                      className="text-white/80 leading-relaxed"
                      style={{ fontFamily: '"Iowan Old Style", Palatino, Georgia, serif', fontSize: '1.0625rem' }}
                    >
                      {line.text}
                    </motion.p>
                  ))}
                </div>

                <p className="text-xs text-white/25 mt-8 leading-relaxed">{data.note}</p>

                <button
                  onClick={() => setDetail(true)}
                  className="w-full mt-8 flex items-center justify-between px-5 py-4 rounded-2xl transition-colors"
                  style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)' }}
                  data-testid="memory-see-all-btn"
                >
                  <span className="text-sm text-white/80">{t('seeAll', lang)}</span>
                  <span className="flex items-center gap-2 text-xs text-white/35">
                    {data.memories.length}
                    <ChevronRight className="w-4 h-4" />
                  </span>
                </button>
              </motion.div>
            ) : (
              <motion.div key="detail" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <h2
                  className="text-2xl text-white/90 mb-6"
                  style={{ fontFamily: '"Iowan Old Style", Palatino, Georgia, serif' }}
                >
                  {t('everything', lang)}
                </h2>
                <div data-testid="memory-detail-list">
                  {data.memories.length === 0 && <p className="text-white/45 text-sm">{t('empty', lang)}</p>}
                  {data.memories.map((item) => (
                    <MemoryCard key={item.memory_id} item={item} lang={lang} onChanged={load} />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </motion.div>
  );
};

export default MemorySummary;
